import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import escapeStringRegexp from 'escape-string-regexp';

import Animation from 'components/Animation';
import PositionedBlock from 'components/PositionedBlock';

import List from './components/List';

class CustomSelect extends React.Component {
	constructor(props) {
		super(props);

		this.onAfterClose = this.onAfterClose.bind(this);
		this.handleSearch = this.handleSearch.bind(this);
		this.closeList = this.closeList.bind(this);
		this.onSelect = this.onSelect.bind(this);
		this.toggleList = this.toggleList.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);

		this.ref = React.createRef();

		this.state = {
			isOpen: false,
			searchValue: ''
		};
	}

	componentDidMount() {
		this.updateControlAriaParameters();
	}

	updateControlAriaParameters() {
		const component = this.ref.current.querySelector(
			`[data-aria-id="${this.props.name}"]`
		);

		if (component) {
			const { options, selectedValue, valueField, defaultTitle } = this.props;
			const selectedOption = _(options).findWhere({
				[valueField]: selectedValue
			});
			const title = selectedOption ? selectedOption.title : defaultTitle;
			const ariaLabel = selectedOption
				? `${this.props.ariaLabel}. Выбрано: ${title}`
				: this.props.ariaLabel;

			component.setAttribute('data-aria-label', ariaLabel);
			component.querySelector('[data-aria-text]').innerText = ariaLabel;
		}
	}

	componentDidUpdate() {
		this.updateControlAriaParameters();
	}

	onSelect(event) {
		const value = event.currentTarget.getAttribute('data-value');
		this.setState({
			isOpen: false,
			selectedValue: value
		});
	}

	onAfterClose() {
		const { selectedValue, searchValue } = this.state;
		const resetValue =
			this.props.resetOption &&
			(this.props.resetOption[this.props.valueField] || '');

		if (
			selectedValue ||
			(this.props.resetOption && selectedValue === resetValue)
		) {
			this.props.onChange?.(this.props.name, this.state.selectedValue);
			this.setState({ selectedValue: null });
		}

		if (searchValue) {
			this.setState({ searchValue: '' });
		}
		const button = this.ref.current.querySelector('button');
		if (selectedValue) {
			const prevAriaLabel = button?.getAttribute('data-aria-label');

			const buttonObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					const newAriaText = mutation.target?.getAttribute('data-aria-label');

					if (
						mutation.type === 'attributes' &&
						(newAriaText !== prevAriaLabel || newAriaText)
					) {
						setTimeout(() => {
							button.focus();
							buttonObserver.disconnect();
						}, 100);
					}
				});
			});

			buttonObserver.observe(button, {
				attributes: true
			});
		} else {
			setTimeout(() => {
				button?.focus();
			}, 100);
		}
	}

	toggleList() {
		this.setState(({ isOpen }) => ({ isOpen: !isOpen }));
	}

	closeList() {
		this.setState({ isOpen: false });
	}

	handleSearch(event) {
		this.setState({ searchValue: event.target.value });
	}

	handleKeyDown(event) {
		if (event.key === 'Escape' || event.key === 'Esc') {
			this.closeList();
		}
	}

	render() {
		const {
			defaultTitle,
			searchable,
			placeholder,
			resetOption,
			selectedValue,
			valueField
		} = this.props;
		let { options } = this.props;
		const { isOpen, searchValue } = this.state;
		const selectedOption = _(options).findWhere({
			[valueField]: selectedValue
		});

		if (searchValue) {
			const searchRegexps = searchValue
				.trim()
				.split(/\s+/)
				.map((part) => new RegExp(escapeStringRegexp(part), 'i'));

			options = options.filter(({ title }) =>
				searchRegexps.every((searchRegexp) => searchRegexp.test(title))
			);
		}

		if (resetOption && !_(resetOption).has(valueField)) {
			resetOption[valueField] = '';
		}

		const title = selectedOption ? selectedOption.title : defaultTitle;

		const Control = React.cloneElement(this.props.controlElement, {
			text: title,
			onClick: this.toggleList
		});

		return (
			<div>
				<div ref={this.ref}>{Control}</div>
				<Animation show={isOpen} onExited={this.onAfterClose}>
					<PositionedBlock
						anchorRef={this.ref}
						onRequestClose={this.closeList}
						offset={25}
						align={this.props.align}
						onKeyDown={this.handleKeyDown}
					>
						<List
							searchable={searchable}
							onSearch={this.handleSearch}
							placeholder={placeholder}
							selectedValue={selectedValue}
							resetOption={resetOption}
							options={options}
							onChange={this.onSelect}
							searchValue={searchValue}
							valueField={valueField}
						/>
					</PositionedBlock>
				</Animation>
			</div>
		);
	}
}

CustomSelect.propTypes = {
	name: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			value: PropTypes.string
		})
	).isRequired,
	selectedValue: PropTypes.node,
	onChange: PropTypes.func,
	defaultTitle: PropTypes.string,
	controlElement: PropTypes.element.isRequired,
	searchable: PropTypes.bool,
	align: PropTypes.oneOf(['right', 'left']),
	placeholder: PropTypes.string,
	resetOption: PropTypes.shape({
		title: PropTypes.string.isRequired,
		value: PropTypes.string
	}),
	valueField: PropTypes.string,
	ariaLabel: PropTypes.string
};

CustomSelect.defaultProps = {
	defaultTitle: '',
	selectedValue: '',
	searchable: false,
	resetOption: { title: 'Показать все' },
	valueField: 'name'
};

export default CustomSelect;
